/**
 * Canvas component renders OST nodes/edges and handles pan/zoom/drag interactions.
 * Also manages the AI slice-preview flow: ghost nodes, TAB cycling, and accept/dismiss.
 */
import { useRef, useState, useEffect, useCallback, useMemo, MouseEvent } from 'react';
import { useStore } from 'store/useStore';
import { NodeType, AiSliceSet } from 'types';
import SlicePanel from 'components/panels/SlicePanel';
import ManualSliceModal from 'components/modals/ManualSliceModal';
import CanvasEdge from './CanvasEdge';
import CanvasNode from './CanvasNode';
import GhostNodeLayer from './GhostNodeLayer';
import PendingNodeLayer from './PendingNodeLayer';
import ConnectionOverlay from './ConnectionOverlay';
import AiPreviewHud from './AiPreviewHud';
import GridLayer from './GridLayer';
import { palette } from 'theme';
import {
    NODE_W,
    NODE_H,
    LAYER_GAP_Y,
    NODE_GAP_X,
    getNodeHeight,
    isValidConnectDragTarget,
} from 'utils/canvasUtils';
import { easeInOutCubic } from 'utils/animationUtils';
import { flattenTreeNodes } from 'utils/ostUtils';
import { screenToCanvasPoint } from 'utils/panZoom';
import useConnectDrag from 'hooks/useConnectDrag';
import useModifierKeyActive from 'hooks/useModifierKeyActive';

type CanvasProps = {
    projectId?: string;
    selectedNode: import('types').OstNode | null;
    onSelectNode: (node: import('types').OstNode | null) => void;
    onAddNode: (type: import('types').NodeType, parentId: string | null, data?: import('types').NewNodePayload) => void;
    pendingNewNodes: { type: import('types').NodeType; parentId: string | null; data: import('types').NewNodePayload; existingNodeId?: string }[];
    onPlacePendingNodes: (position: { x: number; y: number }) => void;
    onCancelPendingNodes: () => void;
    onStartPendingNodes: (nodes: { type: import('types').NodeType; parentId: string | null; data: import('types').NewNodePayload; existingNodeId?: string }[]) => void;
};

export default function Canvas({ projectId, selectedNode, onSelectNode, onAddNode, pendingNewNodes, onPlacePendingNodes, onCancelPendingNodes, onStartPendingNodes }: CanvasProps) {
    const { outcome, opportunities, solutions, userStories, positions, setPosition, solutionChildType, connections, addConnection, reparentNode, insightLinkDrag, addInsightLink, cancelInsightLinkDrag, insights, insightLinks, triageItemDrag, convertNodeToTriageItem, setNodeDragOverTriage, prepareTriageItemForPlacement, cancelTriageItemDrag, triagePanelMinimized, gridSpacing, snapToGrid, canvasView, setCanvasView } = useStore();
    const svgRef = useRef<SVGSVGElement | null>(null);
    // Seeded from the store's remembered view (if this tree already had one fitted/adjusted
    // earlier in the session) so navigating back from Plan/Insights restores it instead of
    // starting from these hardcoded defaults — see the sync effect below and the fit effect's
    // hasFitted check.
    const [pan, setPan] = useState(() => canvasView?.pan ?? { x: 80, y: 60 });
    const [zoom, setZoom] = useState(() => canvasView?.zoom ?? 0.9);
    const [dragging, setDragging] = useState<{ nodeId: string; startMouse: { x: number; y: number }; startPos: { x: number; y: number } } | null>(null);
    const [panning, setPanning] = useState<{ startMouse: { x: number; y: number }; startPan: { x: number; y: number } } | null>(null);
    const [suppressClickNodeId, setSuppressClickNodeId] = useState<string | null>(null);
    // A stored canvasView means this tree was already fitted (or manually adjusted) earlier
    // in the session, so skip the auto-fit-to-view effect below on this mount.
    const [hasFitted, setHasFitted] = useState(() => canvasView !== null);
    // Ctrl on Windows/Linux, Meta/Cmd on Mac — used by CanvasNode to show the hand-pinch cursor
    // when hovering + the modifier is held (see useModifierKeyActive's own doc comment).
    const modifierKeyActive = useModifierKeyActive();
    // Holds the active requestAnimationFrame id for the zoom-fit animation so it can be cancelled.
    const fitAnimRef = useRef<number | null>(null);

    // The Ctrl+drag "connect two nodes" gesture itself (source/target tracking, canvas-space
    // start/current points, the window-level mousemove/mouseup lifecycle) is owned by
    // useConnectDrag — see handleConnectFinish and the `connectDrag` instance below (declared
    // after screenToCanvas, which it depends on) for the OST-specific business rules (parent/child
    // exclusion, dedup, addConnection) layered on top of it.
    const [insightConnectTargetNodeId, setInsightConnectTargetNodeId] = useState<string | null>(null);

    const pinchCursorUrl = `${process.env.PUBLIC_URL || ''}/hand-pinch-cursor.cur`;
    type SlicingTarget = { type: NodeType; node: import('types').OstNode };


    const [slicingTarget, setSlicingTarget] = useState<SlicingTarget | null>(null);
    const [manualSliceTarget, setManualSliceTarget] = useState<import('types').OstNode | null>(null);
    const [pendingNodePos, setPendingNodePos] = useState<{ x: number; y: number } | null>(null);

    // ─── Insight link drag state ─────────────────────────────────────────────
    /** Canvas-space start point of the active insight link drag, computed on drag start. */
    const [insightDragStartCanvas, setInsightDragStartCanvas] = useState<{ x: number; y: number } | null>(null);
    /** Canvas-space current cursor position during an insight link drag. */
    const [insightConnectPoint, setInsightConnectPoint] = useState<{ x: number; y: number } | null>(null);
    /** Ref to hold the hovered opportunity id during insight drag (avoids stale closures). */
    const insightConnectTargetRef = useRef<string | null>(null);
    /** Refs for values consumed inside insight-link drag handlers (avoids stale closures). */
    const panRef = useRef(pan);
    const zoomRef = useRef(zoom);
    const opportunitiesRef = useRef(opportunities);
    const nodeMapRef = useRef<typeof nodeMap>({});

    // ─── Triage item drag state ───────────────────────────────────────────────
    /** Which canvas node ID is currently hovered by the active triage item drag. */
    const [triageDragTargetNodeId, setTriageDragTargetNodeId] = useState<string | null>(null);
    /** Canvas-space start of the active triage item drag (for the preview line). */
    const [, setTriageDragStartCanvas] = useState<{ x: number; y: number } | null>(null);
    /** Canvas-space current cursor position during a triage item drag. */
    const [, setTriageDragPoint] = useState<{ x: number; y: number } | null>(null);
    /** Ref so the triage drag mouseup handler always sees the latest target id. */
    const triageDragTargetRef = useRef<string | null>(null);
    /** Stable ref to avoid adding triagePanelMinimized to the drag useEffect deps. */
    const triagePanelMinimizedRef = useRef(triagePanelMinimized);

    useEffect(() => { triagePanelMinimizedRef.current = triagePanelMinimized; }, [triagePanelMinimized]);

    // ─── Node-on-node drag-to-connect state ───────────────────────────────────
    // Plain (non-Ctrl) node drags: dropping a node onto another node of an equal-or-shallower
    // hierarchy level (see CONNECT_DRAG_LEVEL below) creates the same kind of 'related'
    // NodeConnection the Ctrl+drag gesture does, then hands the dragged node off to the
    // pending-placement ghost flow (see onStartPendingNodes) so the user can pick where it lands.
    /** Which canvas node ID is currently hovered by an in-progress plain node drag. */
    const [connectDropTargetNodeId, setConnectDropTargetNodeId] = useState<string | null>(null);
    /** Ref so the drag mouseup handler always sees the latest hovered target id. */
    const connectDropTargetRef = useRef<string | null>(null);

    // AI preview state: stores suggestion sets returned by the backend
    const [aiPreviewSets, setAiPreviewSets] = useState<AiSliceSet[] | null>(null);
    const [aiActiveSetIndex, setAiActiveSetIndex] = useState(0);
    const [aiTargetNode, setAiTargetNode] = useState<import('types').OstNode | null>(null);
    const [aiNodeType, setAiNodeType] = useState<NodeType | null>(null);
    // Model name reported by the backend (e.g. 'claude-sonnet-4-6') — displayed in the HUD.
    const [aiModel, setAiModel] = useState<string | null>(null);

    // connectPreviewPath (the live Ctrl+drag bezier preview) is declared further down, right after
    // connectDrag — see there for why it can't live here (it needs screenToCanvas, defined later).

    // ─── Insight link drag — preview path ────────────────────────────────────
    /**
     * Bezier preview path connecting the insight-panel drag origin to the current
     * cursor position, rendered on the ConnectionOverlay while dragging.
     */
    const insightConnectPreviewPath = useMemo(() => {
        if (!insightDragStartCanvas || !insightConnectPoint) return null;
        const start = insightDragStartCanvas;
        const end = insightConnectPoint;
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const midX = (start.x + end.x) / 2;
        const midY = (start.y + end.y) / 2;
        const verticalCurve = Math.min(120, Math.max(32, dist * 0.35));
        return `M ${start.x} ${start.y} Q ${midX} ${midY + verticalCurve} ${end.x} ${end.y}`;
    }, [insightDragStartCanvas, insightConnectPoint]);

    const getPos = useCallback(
        (node: import('types').OstNode, index: number, total: number, layerY: number) => {
            if (positions[node.id]) return positions[node.id];
            const totalW = total * NODE_W + (total - 1) * (NODE_GAP_X - NODE_W);
            const startX = -totalW / 2 + NODE_W / 2;
            return { x: startX + index * NODE_GAP_X, y: layerY };
        },
        [positions]
    );

    // While a node-on-node drag-to-connect reparent is in its click-to-place ghost phase, this is
    // the id of the real node standing in for — see the reparent-drop handler below, which is the
    // only place that ever puts an `existingNodeId` entry into pendingNewNodes.
    const pendingRepositionNodeId = pendingNewNodes[0]?.existingNodeId ?? null;

    const allNodes = useMemo(() => {
        const nodes = [];
        if (outcome) {
            nodes.push({ ...outcome, ...getPos(outcome, 0, 1, 0) });
        }
        opportunities.forEach((n, i) => nodes.push({ ...n, ...getPos(n, i, opportunities.length, LAYER_GAP_Y) }));
        solutions.forEach((n, i) => nodes.push({ ...n, ...getPos(n, i, solutions.length, LAYER_GAP_Y * 2) }));
        userStories.forEach((n, i) => nodes.push({ ...n, ...getPos(n, i, userStories.length, LAYER_GAP_Y * 3) }));

        // Substitute the moving ghost's position for the real node's stored one while it's
        // pending placement. Every edge/connection computed from allNodes below (its own parent
        // edge, its children's edges back to it, ConnectionOverlay's Ctrl+drag 'related'
        // connections) reads node positions from here, so they all automatically track the ghost
        // instead of the node's old, already-abandoned spot — with no separate wiring needed.
        // The node itself is skipped at render time (see the CanvasNode map below) so only the
        // dashed ghost (PendingNodeLayer) is visible in the meantime.
        if (pendingRepositionNodeId && pendingNodePos) {
            return nodes.map((n) => (n.id === pendingRepositionNodeId ? { ...n, x: pendingNodePos.x, y: pendingNodePos.y } : n));
        }
        return nodes;
    }, [outcome, opportunities, solutions, userStories, getPos, pendingRepositionNodeId, pendingNodePos]);

    const nodeMap = useMemo(() => Object.fromEntries(allNodes.map((n) => [n.id, n])), [allNodes]);

    // Keep refs up-to-date so insight-link drag handlers always see current values
    useEffect(() => { panRef.current = pan; }, [pan]);
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);

    // Remember the current pan/zoom for the rest of the app session, so switching to the
    // Plan/Insights tab and back restores this exact view instead of the fit effect below
    // running again. Covers both the fit animation's own updates and manual drag/wheel input.
    useEffect(() => {
        setCanvasView({ pan, zoom });
    }, [pan, zoom, setCanvasView]);
    useEffect(() => { opportunitiesRef.current = opportunities; }, [opportunities]);
    useEffect(() => { nodeMapRef.current = nodeMap; }, [nodeMap]);

    const nodeHeights = useMemo(() => Object.fromEntries(allNodes.map((n) => [n.id, getNodeHeight(n)])), [allNodes]);

    const getParentId = useCallback((node: import('types').OstNode, fallbackField?: string): string | null => {
        if (node.parentId) return node.parentId;
        if (fallbackField) {
            const value = (node as unknown as Record<string, unknown>)[fallbackField];
            if (!value) return null;
            if (Array.isArray(value)) {
                return value[0] as string;
            }
            return value as string;
        }
        return null;
    }, []);

    const edges = useMemo(() => {
        const result: Array<{ from: import('types').OstNode; to: import('types').OstNode; relation?: boolean }> = [];

        opportunities.forEach((n) => {
            const pid = getParentId(n, 'outcomeIds');
            if (pid && nodeMap[pid] && nodeMap[n.id]) result.push({ from: nodeMap[pid], to: nodeMap[n.id], relation: false });
        });

        solutions.forEach((n) => {
            const pid = getParentId(n, 'opportunityIds');
            if (pid && nodeMap[pid] && nodeMap[n.id]) result.push({ from: nodeMap[pid], to: nodeMap[n.id], relation: false });
        });

        userStories.forEach((n) => {
            const pid = getParentId(n, 'solutionIds');
            if (pid && nodeMap[pid] && nodeMap[n.id]) result.push({ from: nodeMap[pid], to: nodeMap[n.id], relation: false });
        });

        allNodes.forEach((n) => {
            (n.relations || [])
                .filter((rel) => rel.direction !== 'incoming')
                .forEach((rel) => {
                    const target = nodeMap[rel.nodeId];
                    if (target) result.push({ from: n, to: target, relation: true });
                });
        });

        if (pendingNewNodes.length > 0 && pendingNodePos) {
            pendingNewNodes.forEach((pending, i) => {
                // An existingNodeId entry is a reparent's ghost, not a brand-new node — its real
                // parent edge already tracks the ghost via allNodes' position override above, so
                // adding a synthetic one here would just duplicate it.
                if (pending.existingNodeId) return;

                const parent = pending.parentId ? nodeMap[pending.parentId] : null;
                if (!parent) return;

                result.push({
                    from: parent,
                    to: {
                        id: `pending-node-${i}`,
                        type: pending.type,
                        x: pendingNodePos.x + i * (NODE_W + 25),
                        y: pendingNodePos.y,
                    } as import('types').OstNode,
                    relation: false,
                });
            });
        }

        return result;
    }, [opportunities, solutions, userStories, nodeMap, getParentId, pendingNewNodes, pendingNodePos, allNodes]);

    // Compute initial pan/zoom to fit all nodes in the SVG viewport once, per project load.
    useEffect(() => {
        if (hasFitted) return;
        if (allNodes.length === 0) {
            // No nodes on initial project load: mark as fitted so subsequent adds/deletes do not trigger auto-fit.
            setHasFitted(true);
            return;
        }

        const xs = allNodes.map((n) => n.x);
        const ys = allNodes.map((n) => n.y);
        if (xs.length === 0 || ys.length === 0) return;

        const minX = Math.min(...xs) - NODE_W;
        const maxX = Math.max(...xs) + NODE_W;
        const minY = Math.min(...ys) - NODE_H;
        const maxY = Math.max(...ys) + NODE_H;

        const svg = svgRef.current;
        if (!svg) return;
        const bounds = svg.getBoundingClientRect();
        const availableWidth = bounds.width || window.innerWidth;
        const availableHeight = bounds.height || window.innerHeight;

        const scaleX = availableWidth / (maxX - minX || 1);
        const scaleY = availableHeight / (maxY - minY || 1);
        if (hasFitted) return;

        const targetZoom = Math.max(0.3, Math.min(2, Math.min(scaleX, scaleY) * 0.8));

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        const targetPan = { x: availableWidth / 2 - centerX * targetZoom, y: availableHeight / 2 - centerY * targetZoom };

        // Animate from the current viewport to the fitted target using ease-in-out-cubic.
        // zoomRef and panRef are used to avoid effect dependency churn from active animation.
        const DURATION = 600;
        const startTime = performance.now();
        const startZoom = zoomRef.current;
        const startPan = { x: panRef.current.x, y: panRef.current.y };

        if (fitAnimRef.current !== null) cancelAnimationFrame(fitAnimRef.current);

        const step = (now: number) => {
            const elapsed = now - startTime;
            const t = Math.min(elapsed / DURATION, 1);
            const eased = easeInOutCubic(t);

            setZoom(startZoom + (targetZoom - startZoom) * eased);
            setPan({
                x: startPan.x + (targetPan.x - startPan.x) * eased,
                y: startPan.y + (targetPan.y - startPan.y) * eased,
            });

            if (t < 1) {
                fitAnimRef.current = requestAnimationFrame(step);
            } else {
                fitAnimRef.current = null;
            }
        };

        fitAnimRef.current = requestAnimationFrame(step);
        setHasFitted(true);

        return () => {
            if (fitAnimRef.current !== null) {
                cancelAnimationFrame(fitAnimRef.current);
                fitAnimRef.current = null;
            }
        };
    }, [projectId, hasFitted, allNodes]);

    const onMouseDown = (e: MouseEvent<SVGElement>, nodeId: string) => {
        e.stopPropagation();
        if (modifierKeyActive) {
            connectDrag.start(nodeId, e.clientX, e.clientY);
            return;
        }

        const pos = nodeMap[nodeId] ? { x: nodeMap[nodeId].x, y: nodeMap[nodeId].y } : { x: 0, y: 0 };
        setDragging({ nodeId, startMouse: { x: e.clientX, y: e.clientY }, startPos: pos });
    };

    // Seed pendingNodePos immediately when pending nodes arrive so they are visible
    // before the user moves the mouse (avoids the "invisible until mousemove" bug).
    //
    // Reads nodeMapRef (kept in sync just above) rather than the reactive `nodeMap` on purpose:
    // `allNodes` (and so `nodeMap`) now itself depends on pendingNodePos, to substitute a
    // reparent's ghost position into every edge computed from it (see allNodes' own comment).
    // Depending on `nodeMap` here would close that loop — this effect sets pendingNodePos, which
    // changes nodeMap's reference, which would re-run this effect, forever — even though the
    // *values* involved settle immediately. Depending only on pendingNewNodes still seeds the
    // ghost the instant a new placement starts (nodeMapRef.current is always the latest nodeMap
    // by the time this runs, since its own sync effect above runs first in render order), it just
    // never re-seeds afterward because some unrelated node's position changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (pendingNewNodes.length === 0) {
            setPendingNodePos(null);
            return;
        }

        const parentId = pendingNewNodes[0]?.parentId;
        const parentNode = parentId ? nodeMapRef.current[parentId] : null;
        if (parentNode) {
            setPendingNodePos({ x: parentNode.x, y: parentNode.y + LAYER_GAP_Y });
        }
    }, [pendingNewNodes]);

    const getSnappedPoint = useCallback((x: number, y: number) => {
        const SNAP_THRESHOLD = 14;
        let nextX = x;
        let nextY = y;
        const draggingHeight = NODE_H;
        const draggingTop = nextY - draggingHeight / 2;
        const draggingCenter = nextY;

        allNodes.forEach((n) => {
            const nHeight = getNodeHeight(n);
            const nTop = n.y - nHeight / 2;
            const nCenter = n.y;

            if (Math.abs(n.x - nextX) <= SNAP_THRESHOLD) nextX = n.x;

            const topDelta = Math.abs(nTop - draggingTop);
            const centerDelta = Math.abs(nCenter - draggingCenter);

            if (centerDelta <= SNAP_THRESHOLD && centerDelta <= topDelta) {
                nextY = nCenter;
            } else if (topDelta <= SNAP_THRESHOLD) {
                nextY = nTop + draggingHeight / 2;
            }
        });

        // Apply optional grid snapping (project-scoped)
        if (snapToGrid && gridSpacing && gridSpacing > 0) {
            nextX = Math.round(nextX / gridSpacing) * gridSpacing;
            nextY = Math.round(nextY / gridSpacing) * gridSpacing;
        }

        return { x: nextX, y: nextY };
    }, [allNodes, snapToGrid, gridSpacing]);

    const screenToCanvas = useCallback((clientX: number, clientY: number) => {
        const svg = svgRef.current;
        if (!svg) return null;
        const bounds = svg.getBoundingClientRect();
        return screenToCanvasPoint(bounds, pan, zoom, clientX, clientY);
    }, [pan, zoom]);

    // OST-specific business rules layered on top of the generic useConnectDrag gesture: exclude a
    // direct parent-child pairing (already a tree edge, drawn by CanvasEdge) and duplicate
    // from→to pairs, then persist via the same NodeConnection shape/offset math the inline
    // mouseup handler used to build directly.
    const handleConnectFinish = useCallback((sourceNodeId: string, targetNodeId: string | null, start: { x: number; y: number }, end: { x: number; y: number }) => {
        if (!targetNodeId || targetNodeId === sourceNodeId) return;
        const source = nodeMap[sourceNodeId];
        const target = nodeMap[targetNodeId];
        if (!source || !target) return;
        if (source.parentId === target.id || target.parentId === source.id) return;

        const existing = connections.some((c) => c.fromNodeId === sourceNodeId && c.toNodeId === targetNodeId);
        if (existing) return;

        const connectionId = `conn_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
        addConnection({
            id: connectionId,
            fromNodeId: sourceNodeId,
            toNodeId: targetNodeId,
            type: 'related',
            fromOffset: { x: start.x - source.x, y: start.y - source.y },
            toOffset: { x: end.x - target.x, y: end.y - target.y },
        });
    }, [nodeMap, connections, addConnection]);

    const connectDrag = useConnectDrag({ screenToCanvas, onFinish: handleConnectFinish });

    const connectPreviewPath = useMemo(() => {
        if (!connectDrag.active || !connectDrag.startPoint || !connectDrag.currentPoint) return null;
        const { startPoint, currentPoint } = connectDrag;

        const dx = currentPoint.x - startPoint.x;
        const dy = currentPoint.y - startPoint.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const midX = (startPoint.x + currentPoint.x) / 2;
        const midY = (startPoint.y + currentPoint.y) / 2;

        // Gravity-style downward curve: always bend down, independent of source/target orientation.
        const verticalCurve = Math.min(120, Math.max(32, dist * 0.35));
        const controlX = midX;
        const controlY = midY + verticalCurve;

        return `M ${startPoint.x} ${startPoint.y} Q ${controlX} ${controlY} ${currentPoint.x} ${currentPoint.y}`;
        // Deps are the individual fields actually read, not `connectDrag` itself — useConnectDrag
        // returns a fresh object every render, so depending on the whole object would recompute
        // this on every render instead of only when the drag geometry actually changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectDrag.active, connectDrag.startPoint, connectDrag.currentPoint]);

    const resetConnect = () => {
        connectDrag.cancel();
        setInsightConnectTargetNodeId(null);
    };

    const onSvgMouseDown = (e: MouseEvent<SVGSVGElement | Element>) => {
        if (connectDrag.active) {
            resetConnect();
            return;
        }

        if (pendingNewNodes.length > 0) {
            const coords = screenToCanvas(e.clientX, e.clientY);
            if (coords) {
                const snapped = getSnappedPoint(coords.x, coords.y);
                onPlacePendingNodes(snapped);
                setPendingNodePos(null);
            }
            setAiPreviewSets(null);
            setAiTargetNode(null);
            setAiNodeType(null);
            e.preventDefault();
            return;
        }

        const target = e.target as Element;
        if (target === svgRef.current || target.tagName === 'svg' || target.tagName === 'line') {
            onSelectNode(null);
            setPanning({ startMouse: { x: e.clientX, y: e.clientY }, startPan: { ...pan } });
        }
    };

    const onSvgMouseMove = (e: MouseEvent<SVGSVGElement | Element>) => {
        // Ctrl+drag point-tracking is handled globally by useConnectDrag's own window listener
        // (see connectDrag above) — it doesn't need this SVG-local mousemove at all.
        if (connectDrag.active) return;

        if (pendingNewNodes.length === 0) return;
        const coords = screenToCanvas(e.clientX, e.clientY);
        if (!coords) return;

        const snapped = getSnappedPoint(coords.x, coords.y);
        setPendingNodePos(snapped);
    };

    useEffect(() => {
        const onMove = (e: globalThis.MouseEvent) => {
            if (pendingNewNodes.length > 0) {
                const coords = screenToCanvas(e.clientX, e.clientY);
                if (coords) {
                    const snapped = getSnappedPoint(coords.x, coords.y);
                    setPendingNodePos(snapped);
                }
                return;
            }

            // Ctrl+drag point-tracking during connectDrag.active is handled by useConnectDrag's
            // own window listener — dragging/panning are never set while it's active (onMouseDown
            // returns early into connectDrag.start instead of setDragging), so no explicit guard
            // is needed here for those branches below.

            if (dragging) {
                const dx = (e.clientX - dragging.startMouse.x) / zoom;
                const dy = (e.clientY - dragging.startMouse.y) / zoom;
                let nextX = dragging.startPos.x + dx;
                let nextY = dragging.startPos.y + dy;

                const SNAP_THRESHOLD = 14;
                const draggingHeight = getNodeHeight(nodeMap[dragging.nodeId] || {});
                const draggingTop = nextY - draggingHeight / 2;
                const draggingCenter = nextY;

                allNodes.forEach((n) => {
                    if (n.id === dragging.nodeId) return;
                    const nHeight = getNodeHeight(n);
                    const nTop = n.y - nHeight / 2;
                    const nCenter = n.y;

                    if (Math.abs(n.x - nextX) <= SNAP_THRESHOLD) nextX = n.x;

                    const topDelta = Math.abs(nTop - draggingTop);
                    const centerDelta = Math.abs(nCenter - draggingCenter);

                    if (centerDelta <= SNAP_THRESHOLD && centerDelta <= topDelta) {
                        nextY = nCenter;
                    } else if (topDelta <= SNAP_THRESHOLD) {
                        nextY = nTop + draggingHeight / 2;
                    }
                });

                setPosition(dragging.nodeId, { x: nextX, y: nextY });

                // Hit-test compatible connect-drop targets so a plain node drag can reparent a
                // node the same way dropping it does — see connectDropTargetNodeId and
                // utils/canvasUtils's isValidConnectDragTarget for the hierarchy rule (one level
                // shallower, or opportunity onto opportunity for sub-opportunity nesting).
                const source = nodeMap[dragging.nodeId];
                let connectHit: string | null = null;
                if (source) {
                    const svgBounds = svgRef.current?.getBoundingClientRect();
                    const pt = svgBounds ? screenToCanvasPoint(svgBounds, panRef.current, zoomRef.current, e.clientX, e.clientY) : null;
                    if (pt) {
                        const candidate = allNodes.find((n) => {
                            if (!isValidConnectDragTarget(source, n, (id) => nodeMap[id]?.parentId)) return false;
                            const h = getNodeHeight(n);
                            return pt.x >= n.x - NODE_W / 2 && pt.x <= n.x + NODE_W / 2 && pt.y >= n.y - h / 2 && pt.y <= n.y + h / 2;
                        });
                        connectHit = candidate?.id ?? null;
                    }
                }
                connectDropTargetRef.current = connectHit;
                setConnectDropTargetNodeId(connectHit);

                // Track whether the dragged node is hovering over the triage panel for visual feedback.
                const triageEl = document.querySelector('.triage-panel');
                if (triageEl && !triagePanelMinimizedRef.current) {
                    const triageRect = triageEl.getBoundingClientRect();
                    const overTriage =
                        e.clientX >= triageRect.left && e.clientX <= triageRect.right &&
                        e.clientY >= triageRect.top && e.clientY <= triageRect.bottom;
                    setNodeDragOverTriage(overTriage);
                } else {
                    setNodeDragOverTriage(false);
                }
            }
            if (panning) {
                setPan({ x: panning.startPan.x + e.clientX - panning.startMouse.x, y: panning.startPan.y + e.clientY - panning.startMouse.y });
            }
        };

        const onUp = (e: globalThis.MouseEvent) => {
            // If a node was being dragged and dropped onto the triage panel, convert it to a triage item.
            if (dragging && !triagePanelMinimizedRef.current) {
                const triageEl = document.querySelector('.triage-panel');
                if (triageEl) {
                    const triageRect = triageEl.getBoundingClientRect();
                    const overTriage =
                        e.clientX >= triageRect.left && e.clientX <= triageRect.right &&
                        e.clientY >= triageRect.top && e.clientY <= triageRect.bottom;
                    if (overTriage) {
                        // convertNodeToTriageItem returns false when node has children — silently ignore.
                        convertNodeToTriageItem(dragging.nodeId);
                        setNodeDragOverTriage(false);
                        setDragging(null);
                        setPanning(null);
                        return;
                    }
                }
                setNodeDragOverTriage(false);
            }

            // If the node was dropped onto a valid connect-drag target, reparent it under that
            // target (a real structural move — either the target is exactly one level shallower,
            // e.g. a solution dropped on an opportunity, or both are opportunities, i.e.
            // sub-opportunity nesting — see isValidConnectDragTarget). Then snap the dragged node
            // back to its start position and hand it to the pending-placement ghost flow so the
            // user can click to choose where it lands — the same flow a Triage-panel drop already
            // uses (see onStartPendingNodes above).
            if (dragging && connectDropTargetRef.current) {
                const targetId = connectDropTargetRef.current;
                const sourceId = dragging.nodeId;
                const source = nodeMap[sourceId];
                const target = nodeMap[targetId];
                if (source && target && isValidConnectDragTarget(source, target, (id) => nodeMap[id]?.parentId)) {
                    reparentNode(sourceId, targetId);
                    setPosition(sourceId, dragging.startPos);
                    onStartPendingNodes([{
                        type: source.type,
                        // The node now lives under targetId — carry that through to the ghost's
                        // edge preview too, instead of its old parent.
                        parentId: targetId,
                        data: { title: source.title, description: source.description },
                        existingNodeId: sourceId,
                    }]);
                }
                connectDropTargetRef.current = null;
                setConnectDropTargetNodeId(null);
                setDragging(null);
                setPanning(null);
                return;
            }

            // If we were dragging a node (moved the mouse), suppress the subsequent click
            // so the node isn't selected on mouseup after a drag. Use a small threshold
            // to distinguish a true drag from a simple click.
            if (dragging) {
                const dx = e.clientX - dragging.startMouse.x;
                const dy = e.clientY - dragging.startMouse.y;
                const moved = Math.hypot(dx, dy) > 4; // pixels
                if (moved) {
                    setSuppressClickNodeId(dragging.nodeId);
                }
            }

            // Ctrl+drag resolution (valid-target/dedup/parent-child checks + addConnection) is
            // handled by useConnectDrag's own mouseup listener, which calls handleConnectFinish —
            // see connectDrag above.
            connectDropTargetRef.current = null;
            setConnectDropTargetNodeId(null);
            setDragging(null);
            setPanning(null);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);

        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [dragging, panning, zoom, setPosition, allNodes, nodeMap, pendingNewNodes, getSnappedPoint, screenToCanvas, convertNodeToTriageItem, setNodeDragOverTriage, reparentNode, onStartPendingNodes]);

    // Apply a body class for the entire duration of a canvas node drag so side panels
    // can lower their z-index and let the dragged node paint on top of them.
    useEffect(() => {
        if (dragging) {
            document.body.classList.add('canvas-node-dragging');
        } else {
            document.body.classList.remove('canvas-node-dragging');
        }
        return () => {
            document.body.classList.remove('canvas-node-dragging');
        };
    }, [dragging]);

    const onWheel = (e: WheelEvent) => {
        e.preventDefault();

        const svg = svgRef.current;
        if (!svg) return;

        const bounds = svg.getBoundingClientRect();
        const mouseX = e.clientX - bounds.left;
        const mouseY = e.clientY - bounds.top;

        const oldZoom = zoom;
        const factor = e.deltaY < 0 ? 1.1 : 0.9;
        const newZoom = Math.min(2, Math.max(0.3, oldZoom * factor));
        if (newZoom === oldZoom) return;

        const canvasX = (mouseX - pan.x) / oldZoom;
        const canvasY = (mouseY - pan.y) / oldZoom;

        const newPanX = mouseX - canvasX * newZoom;
        const newPanY = mouseY - canvasY * newZoom;

        setZoom(newZoom);
        setPan({ x: newPanX, y: newPanY });
    };

    // Reset fit state when project changes so layout can re-center
    useEffect(() => {
        setHasFitted(false);
    }, [projectId]);

    // Clear AI preview and pending mode whenever the project changes so stale state is never shown
    useEffect(() => {
        setAiPreviewSets(null);
        setAiTargetNode(null);
        setAiNodeType(null);
        onCancelPendingNodes();
    }, [projectId, onCancelPendingNodes]);

    // Preload cursor files so the first Ctrl press does not show the fallback cursor.
    useEffect(() => {
        const base = process.env.PUBLIC_URL || '';
        const cursorUrls = [`${base}/hand-cursor.cur`, `${base}/hand-pinch-cursor.cur`];

        const links = cursorUrls.map((href) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = href;
            document.head.appendChild(link);
            return link;
        });

        return () => {
            links.forEach((link) => link.remove());
        };
    }, []);

    // Ensure the cursor stays as hand-pinch during the entire connect line drag interaction.
    // body.style.cursor sets the actual URL; the CSS class forces all children to inherit it,
    // preventing per-element cursor declarations from overriding during the drag.
    useEffect(() => {
        if (!connectDrag.active) return;
        document.body.style.cursor = `url(${pinchCursorUrl}) 16 16, crosshair`;
        document.body.classList.add('dragging-pinch');
        return () => {
            document.body.style.cursor = '';
            document.body.classList.remove('dragging-pinch');
        };
    }, [connectDrag.active, pinchCursorUrl]);

    // Show pinch cursor globally while an insight link drag is in progress.
    useEffect(() => {
        if (!insightLinkDrag) return;
        document.body.style.cursor = `url(${pinchCursorUrl}) 16 16, crosshair`;
        document.body.classList.add('dragging-pinch');
        return () => {
            document.body.style.cursor = '';
            document.body.classList.remove('dragging-pinch');
        };
    }, [insightLinkDrag, pinchCursorUrl]);

    /**
     * Manages the insight link drag gesture: when insightLinkDrag is set by the
     * InsightsPanel, Canvas translates the screen-space origin to canvas space,
     * tracks the cursor via global handlers, hit-tests opportunity nodes on every
     * mousemove, and creates an InsightLink on mouseup over an opportunity.
     */
    useEffect(() => {
        if (!insightLinkDrag) {
            setInsightConnectPoint(null);
            setInsightDragStartCanvas(null);
            insightConnectTargetRef.current = null;
            setInsightConnectTargetNodeId(null);
            return;
        }

        // Compute the drag origin in canvas coordinates using current pan/zoom
        const svg = svgRef.current;
        if (!svg) return;
        const bounds = svg.getBoundingClientRect();
        const startCanvas = screenToCanvasPoint(bounds, panRef.current, zoomRef.current, insightLinkDrag.startScreenX, insightLinkDrag.startScreenY);
        setInsightDragStartCanvas(startCanvas);
        setInsightConnectPoint(startCanvas);

        const onMove = (e: globalThis.MouseEvent) => {
            const svgEl = svgRef.current;
            if (!svgEl) return;
            const b = svgEl.getBoundingClientRect();
            const pt = screenToCanvasPoint(b, panRef.current, zoomRef.current, e.clientX, e.clientY);
            setInsightConnectPoint(pt);

            // Hit-test: find the opportunity node the cursor is currently over
            const currentNodeMap = nodeMapRef.current;
            const hit = opportunitiesRef.current.find((opp) => {
                const pos = currentNodeMap[opp.id];
                if (!pos) return false;
                const h = getNodeHeight(opp);
                return (
                    pt.x >= pos.x - NODE_W / 2 &&
                    pt.x <= pos.x + NODE_W / 2 &&
                    pt.y >= pos.y - h / 2 &&
                    pt.y <= pos.y + h / 2
                );
            });
            insightConnectTargetRef.current = hit?.id ?? null;
            setInsightConnectTargetNodeId(hit?.id ?? null);
        };

        const onUp = () => {
            const targetId = insightConnectTargetRef.current;
            if (targetId) {
                // Connect the highlighted text snippet to the hovered opportunity
                addInsightLink(
                    insightLinkDrag.insightId,
                    insightLinkDrag.selectedText,
                    insightLinkDrag.selectionStart,
                    insightLinkDrag.selectionEnd,
                    targetId,
                );
            }
            setInsightConnectTargetNodeId(null);
            cancelInsightLinkDrag();
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [insightLinkDrag, addInsightLink, cancelInsightLinkDrag]);

    /**
     * Triage item drag: when the user drags a card from the Triage panel onto the canvas,
     * we listen for mousemove/mouseup globally, hit-test tree nodes, and on a successful
     * drop call convertTriageItemToNode to create the appropriate child node.
     */
    useEffect(() => {
        if (!triageItemDrag) {
            setTriageDragTargetNodeId(null);
            setTriageDragPoint(null);
            setTriageDragStartCanvas(null);
            return;
        }

        const svgEl = svgRef.current;
        if (!svgEl) return;
        const svgBounds = svgEl.getBoundingClientRect();

        // Valid drop targets: nodes that can accept children (not story/experiment)
        const validTargetNodes = [
            ...(outcome ? [outcome] : []),
            ...opportunities,
            ...solutions,
        ];

        const onMove = (e: globalThis.MouseEvent) => {
            const pt = screenToCanvasPoint(svgBounds, panRef.current, zoomRef.current, e.clientX, e.clientY);
            setTriageDragPoint(pt);

            const currentNodeMap = nodeMapRef.current;
            const hit = validTargetNodes.find((node) => {
                const pos = currentNodeMap[node.id];
                if (!pos) return false;
                const h = getNodeHeight(node);
                return (
                    pt.x >= pos.x - NODE_W / 2 &&
                    pt.x <= pos.x + NODE_W / 2 &&
                    pt.y >= pos.y - h / 2 &&
                    pt.y <= pos.y + h / 2
                );
            });
            triageDragTargetRef.current = hit?.id ?? null;
            setTriageDragTargetNodeId(hit?.id ?? null);
        };

        const onUp = () => {
            const targetId = triageDragTargetRef.current;
            if (targetId) {
                // Remove the triage item and enter pending-node placement mode so the
                // user can click the exact canvas position for the new node.
                const pendingData = prepareTriageItemForPlacement(triageItemDrag.itemId, targetId);
                if (pendingData) {
                    onStartPendingNodes([{
                        type: pendingData.type,
                        parentId: pendingData.parentId,
                        data: { title: pendingData.title, description: pendingData.description },
                    }]);
                }
            }
            // Always clear drag state regardless of whether a node was hit
            cancelTriageItemDrag();
            triageDragTargetRef.current = null;
            setTriageDragTargetNodeId(null);
            setTriageDragPoint(null);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [triageItemDrag, outcome, opportunities, solutions, prepareTriageItemForPlacement, cancelTriageItemDrag, onStartPendingNodes]);

    /**
     * TAB key handler for cycling AI slice-set previews.
     * Only active while an AI preview is visible; does not interfere with normal Tab behaviour.
     * Enter accepts the current set (converts to pending nodes), Escape dismisses.
     */
    useEffect(() => {
        if (!aiPreviewSets || !aiTargetNode || !aiNodeType) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                e.preventDefault();
                const nextIndex = e.shiftKey
                    ? (aiActiveSetIndex - 1 + aiPreviewSets.length) % aiPreviewSets.length
                    : (aiActiveSetIndex + 1) % aiPreviewSets.length;

                setAiActiveSetIndex(nextIndex);

                const nextSet = aiPreviewSets[nextIndex];
                if (!nextSet) return;

                const pendingItems = nextSet.slices.map((slice) => ({
                    type: aiNodeType,
                    parentId: aiTargetNode.id,
                    data: {
                        title: slice.title,
                        description: slice.description,
                        moscow: 'Could have',
                        // Structured story/experiment fields populated by the AI
                        asA: slice.asA,
                        iWant: slice.iWant,
                        soThat: slice.soThat,
                        acceptanceCriteria: slice.acceptanceCriteria,
                        assumption: slice.assumption,
                        method: slice.method,
                        successMetric: slice.successMetric,
                    },
                })) as Array<{ type: NodeType; parentId: string | null; data: import('types').NewNodePayload }>;

                onStartPendingNodes(pendingItems);
            } else if (e.key === 'Escape') {
                setAiPreviewSets(null);
                setAiTargetNode(null);
                setAiNodeType(null);
                onCancelPendingNodes();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [aiPreviewSets, aiActiveSetIndex, aiTargetNode, aiNodeType, onStartPendingNodes, onCancelPendingNodes]);

    // Attach wheel handler for zooming to the SVG and clean up on unmount
    useEffect(() => {
        const el = svgRef.current;
        if (!el) return;
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    });

    const transform = `translate(${pan.x}, ${pan.y}) scale(${zoom})`;

    // Build ancestor chain for AI slice context before the return so it stays readable.
    const sliceAncestors: import('types').OstNode[] = [];
    if (slicingTarget) {
        const allOstNodes = flattenTreeNodes({ outcome, opportunities, solutions, userStories });
        const nodeById = Object.fromEntries(allOstNodes.map((n) => [n.id, n]));
        let current: import('types').OstNode | null = slicingTarget.node;
        while (current?.parentId && nodeById[current.parentId]) {
            const parent: import('types').OstNode = nodeById[current.parentId];
            sliceAncestors.unshift(parent);
            current = parent;
        }
        if (outcome && (sliceAncestors.length === 0 || sliceAncestors[0].id !== outcome.id)) {
            sliceAncestors.unshift(outcome);
        }
    }

    // Collect insight nodes linked to any node in the slicing hierarchy (ancestors + target).
    // These provide additional evidence and context to the AI when generating slice suggestions.
    const hierarchyInsights: import('types').InsightNode[] = (() => {
        if (!slicingTarget || !insights?.length || !insightLinks?.length) return [];

        const hierarchyNodeIds = new Set<string>(
            [...sliceAncestors, slicingTarget.node].map((n) => n.id),
        );

        // Find unique insight IDs that have at least one link into the hierarchy
        const linkedInsightIds = new Set<string>();
        for (const link of insightLinks) {
            if (link.targetNodeIds.some((id) => hierarchyNodeIds.has(id))) {
                linkedInsightIds.add(link.insightId);
            }
        }

        return insights.filter((insight) => linkedInsightIds.has(insight.id));
    })();

    // Collect nodes that share a soft (drag-connect) connection with the slicing target.
    // Both incoming (peer → target) and outgoing (target → peer) directions are included,
    // as each direction surfaces adjacent relational context about what the user is building.
    // Nodes already in the ancestor chain are excluded to avoid duplicating hierarchy context.
    const sliceConnectedNodes: { direction: 'incoming' | 'outgoing'; node: import('types').OstNode }[] = (() => {
        if (!slicingTarget || !connections?.length) return [];

        const targetId = slicingTarget.node.id;
        const allOstNodes = flattenTreeNodes({ outcome, opportunities, solutions, userStories });
        const nodeById = Object.fromEntries(allOstNodes.map((n) => [n.id, n]));

        // Exclude the target itself and any ancestor (already covered by hierarchy context)
        const excludeIds = new Set<string>([
            targetId,
            ...sliceAncestors.map((n) => n.id),
        ]);

        const result: { direction: 'incoming' | 'outgoing'; node: import('types').OstNode }[] = [];
        const seenNodeIds = new Set<string>();

        for (const conn of connections) {
            if (conn.toNodeId === targetId) {
                const peer = nodeById[conn.fromNodeId];
                if (peer && !excludeIds.has(peer.id) && !seenNodeIds.has(peer.id)) {
                    result.push({ direction: 'incoming', node: peer });
                    seenNodeIds.add(peer.id);
                }
            }
            if (conn.fromNodeId === targetId) {
                const peer = nodeById[conn.toNodeId];
                if (peer && !excludeIds.has(peer.id) && !seenNodeIds.has(peer.id)) {
                    result.push({ direction: 'outgoing', node: peer });
                    seenNodeIds.add(peer.id);
                }
            }
        }

        return result;
    })();

    return (
        <div className='canvas-wrapper'>

            {/* AI preview HUD — shows current lens label, cycle counter, and key hints */}
            <AiPreviewHud
                aiPreviewSets={aiPreviewSets}
                activeSetIndex={aiActiveSetIndex}
                aiModel={aiModel}
                onDismiss={() => { setAiPreviewSets(null); setAiTargetNode(null); setAiNodeType(null); setAiModel(null); }}
            />

            <svg
                ref={svgRef}
                className='canvas-svg'
                onMouseDown={onSvgMouseDown}
                onMouseMove={onSvgMouseMove}
                style={connectDrag.active ? { cursor: `url(${pinchCursorUrl}) 16 16, grab` } : undefined}
            >
                <defs>
                    {/* Soft drop shadow applied to node rects (see CanvasNode.tsx) */}
                    <filter id='node-shadow' x='-30%' y='-30%' width='160%' height='160%'>
                        <feDropShadow dx='0' dy='2' stdDeviation='2.5' floodColor='#000000' floodOpacity='0.2' />
                    </filter>
                </defs>

                <g transform={transform}>

                    {/* Background dot grid (SVG pattern fill - single rect). Only `spacing` is
                        overridden — it's the user-adjustable snap grid (useStore's gridSpacing);
                        radius/color/opacity/extent are left at GridLayer's own defaults (theme.ts's
                        ostGridDot* constants), the single source of truth for this look. */}
                    <GridLayer spacing={gridSpacing} />

                    {/* Dashed ghost rectangles that follow the cursor during pending-node placement */}
                    <PendingNodeLayer pendingNewNodes={pendingNewNodes} pendingNodePos={pendingNodePos} />

                    <CanvasEdge edges={edges} nodeHeights={nodeHeights} />

                    {/* The node standing in for a pending-placement ghost is skipped here — only
                        its dashed ghost (PendingNodeLayer above) is shown until it's placed. */}
                    {allNodes.filter((node) => node.id !== pendingRepositionNodeId).map((node) => (
                        <CanvasNode
                            key={node.id}
                            node={node}
                            selectedNode={selectedNode}
                            nodeHeights={nodeHeights}
                            solutionChildType={solutionChildType}
                            modifierKeyActive={modifierKeyActive}
                            isConnectionSource={connectDrag.active && node.id === connectDrag.sourceId}
                            isConnectionTarget={connectDrag.active && node.id === connectDrag.targetId}
                            isInsightConnectionTarget={node.id === insightConnectTargetNodeId}
                            isTriageDragTarget={node.id === triageDragTargetNodeId}
                            isNodeConnectDropTarget={node.id === connectDropTargetNodeId}
                            connectMode={connectDrag.active}
                            onConnectHover={(nid) => {
                                const activeConnect = connectDrag.active || !!insightLinkDrag;
                                if (!activeConnect || nid === connectDrag.sourceId) return;

                                if (connectDrag.active) {
                                    const source = connectDrag.sourceId ? nodeMap[connectDrag.sourceId] : null;
                                    const target = nodeMap[nid];
                                    if (!source || !target) return;
                                    if (source.parentId === target.id || target.parentId === source.id) {
                                        // Exclude direct parent-child connect — leave no target
                                        // hovered rather than highlighting a pairing
                                        // handleConnectFinish would reject anyway.
                                        connectDrag.setHoverTarget(null);
                                        return;
                                    }
                                }

                                connectDrag.setHoverTarget(nid);
                                if (insightLinkDrag) {
                                    setInsightConnectTargetNodeId(nid);
                                }
                            }}
                            onConnectLeave={() => {
                                if (connectDrag.active) {
                                    connectDrag.setHoverTarget(null);
                                }
                            }}
                            onMouseDown={onMouseDown}
                            onSelectNode={onSelectNode}
                            onAddNode={onAddNode}
                            onSlice={(type: NodeType, n: import('types').OstNode) => setSlicingTarget({ type, node: n })}
                            onManualSlice={(n: import('types').OstNode) => setManualSliceTarget(n)}
                            suppressClickNodeId={suppressClickNodeId}
                            onConsumeSuppressedClick={() => setSuppressClickNodeId(null)}
                        />
                    ))}

                    {/* AI ghost edges + ghost nodes during slice preview */}
                    <GhostNodeLayer
                        aiPreviewSets={aiPreviewSets}
                        activeSetIndex={aiActiveSetIndex}
                        aiTargetNode={aiTargetNode}
                        aiNodeType={aiNodeType}
                        hasPendingNodes={pendingNewNodes.length > 0}
                        nodeMap={nodeMap as Record<string, import('types').OstNode & { x: number; y: number }>}
                        nodeHeights={nodeHeights}
                    />

                    {!outcome && (
                        <g onClick={() => onAddNode('outcome', null)} className='clickable'>
                            <rect
                                x={-110}
                                y={-35}
                                width={220}
                                height={70}
                                rx={2}
                                fill={palette.surface}
                                stroke={palette.border}
                                strokeWidth={1}
                                strokeDasharray='4 3'
                            />
                            <text x={0} y={-6} fill={palette.muted} fontSize={12} textAnchor='middle' style={{ fontFamily: 'var(--font-monospace)' }}>
                                Click to add
                            </text>
                            <text x={0} y={14} fill={palette.accentDark} fontSize={13} fontWeight={600} textAnchor='middle' style={{ fontFamily: 'var(--font-monospace)' }}>
                                + Outcome
                            </text>
                        </g>
                    )}

                </g>
            </svg>

            {/* Separate SVG overlay for non-hierarchical connections so lines never clip inside the canvas transform group */}
            <ConnectionOverlay
                connections={connections}
                connectPreviewPath={connectPreviewPath ?? insightConnectPreviewPath}
                transform={transform}
                nodeMap={nodeMap as Record<string, import('types').OstNode & { x: number; y: number }>}
            />

            {/* Slice panel uses the ancestor chain pre-computed before the return */}
            {slicingTarget && (
                <SlicePanel
                    type={slicingTarget.type}
                    parentNode={slicingTarget.node}
                    onClose={() => setSlicingTarget(null)}
                    outcomeTitle={outcome?.title}
                    ancestorNodes={sliceAncestors}
                    hierarchyInsights={hierarchyInsights}
                    connectedNodes={sliceConnectedNodes}
                    onAiSuggest={(sets: AiSliceSet[], model?: string) => {
                        if (sets.length === 0) {
                            setSlicingTarget(null);
                            return;
                        }

                        setAiPreviewSets(sets);
                        setAiActiveSetIndex(0);
                        setAiTargetNode(slicingTarget.node);
                        setAiNodeType(slicingTarget.type);
                        setAiModel(model || null);

                        const firstSet = sets[0];
                        const pendingItems = firstSet.slices.map((slice) => ({
                            type: slicingTarget.type,
                            parentId: slicingTarget.node.id,
                            data: {
                                title: slice.title,
                                description: slice.description,
                                moscow: 'Could have',
                                // Structured story/experiment fields populated by the AI
                                asA: slice.asA,
                                iWant: slice.iWant,
                                soThat: slice.soThat,
                                acceptanceCriteria: slice.acceptanceCriteria,
                                assumption: slice.assumption,
                                method: slice.method,
                                successMetric: slice.successMetric,
                            },
                        })) as Array<{ type: import('types').NodeType; parentId: string | null; data: import('types').NewNodePayload }>;

                        onStartPendingNodes(pendingItems);
                        setSlicingTarget(null);
                    }}
                    onCreate={(items: Array<import('types').NewNodePayload>) => {
                        // Map slicingTarget.type to the correct child node type.
                        // Opportunity slices produce opportunities; experiment stays experiment;
                        // everything else (including solution-epic slices) produces stories.
                        const childType: import('types').NodeType =
                            slicingTarget.type === 'opportunity'
                                ? 'opportunity'
                                : slicingTarget.type === 'experiment'
                                    ? 'experiment'
                                    : 'story';
                        const pendingItems = items.map((item) => ({
                            type: childType,
                            parentId: slicingTarget.node.id,
                            data: item,
                        })) as Array<{ type: import('types').NodeType; parentId: string | null; data: import('types').NewNodePayload }>;

                        onStartPendingNodes(pendingItems);
                        setSlicingTarget(null);
                    }}
                />
            )}

            {manualSliceTarget && (
                <ManualSliceModal
                    parentNode={manualSliceTarget}
                    onClose={() => setManualSliceTarget(null)}
                    onCreate={(rows) => {
                        // Each row carries its own resolved type, unlike SlicePanel's onCreate
                        // above which hoists one shared childType for the whole batch.
                        const pendingItems = rows.map((row) => ({
                            type: row.type,
                            parentId: manualSliceTarget.id,
                            data: { title: row.title },
                        })) as Array<{ type: import('types').NodeType; parentId: string | null; data: import('types').NewNodePayload }>;

                        onStartPendingNodes(pendingItems);
                        setManualSliceTarget(null);
                    }}
                />
            )}
        </div>
    );
}
